import { isProductInStock } from 'components/ProductDetails';
import getConfig from 'next/config';
import {
  CachedProductListResponse,
  Product,
  ProductAggregation,
  ProductCache,
  SearchCriteria,
  TBreadCrumb,
} from 'types/';
import RestUtility from '../../restUtility';

import {
  dsoProductsEndPoint,
  productCacheEndpoint,
} from '../../restUtility/restEndpoints';
import { notifyError } from 'config/setupBugsnag';
import { AppliedFacetFilter } from 'components/FacetFilters';
import { productService } from 'services';
import { isNAStore } from 'utils';

export class ProductCacheService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async getProductDetailsUsingCache(slug: string, isDSOStore = false) {
    const endpoint = isDSOStore
      ? `${dsoProductsEndPoint}`
      : productCacheEndpoint;
    const response = await this.restUtility.get<ProductCache>(
      `${endpoint}/${slug}`
    );

    const quantityLimitResponse = await productService.getProductQuantityLimit(
      response.sku,
      isDSOStore
    );

    return this.buildProductDetailsForClient(response, quantityLimitResponse);
  }

  async getProductsUsingCache(
    searchCriteria: SearchCriteria,
    filters: AppliedFacetFilter[],
    sortDirection: string | undefined,
    pageSize: number | undefined,
    currentPageNumber: number,
    isDsoStore = false
  ) {
    try {
      const { publicRuntimeConfig } = getConfig();
      const perPageSize = pageSize ?? publicRuntimeConfig.LISTING_PAGE_SIZE;
      const pageParams = `?page=${currentPageNumber}&per_page=${perPageSize}`;

      const searchParams = this.buildSearchParams(searchCriteria);
      const filterParams = this.buildFilterParams(filters);
      const aggregationParams = this.buildAggregationParams(
        searchCriteria.taxonSlug,
        isDsoStore
      );

      const productsEndpoint = isDsoStore
        ? dsoProductsEndPoint
        : productCacheEndpoint;

      let url = `${productsEndpoint}${pageParams}&${searchParams}`;

      if (filterParams.length > 0) {
        url += `&${filterParams}`;
      }

      if (aggregationParams.length > 0) {
        url += `&${aggregationParams}`;
      }

      if (sortDirection) {
        url += `&sort=${sortDirection}`;
      }

      const response = await this.restUtility.get<CachedProductListResponse>(
        url
      );

      return this.buildProductsForClient(response, currentPageNumber);
    } catch (error) {
      notifyError(
        `Error fetching products from cache searchCriteria::${JSON.stringify(
          searchCriteria
        )} filters::${JSON.stringify(
          filters
        )} sortDirection::${sortDirection} pageSize::${pageSize} currentPageNumber::${currentPageNumber}`,
        error
      );
    }
  }

  buildProductsForClient(
    response: CachedProductListResponse,
    currentPageNumber?: number
  ) {
    const transformedPrdouctsResponse: {
      products: Product[];
      aggregations: ProductAggregation[];
      translations: Record<string, string>;
      count: number;
      totalCount: number;
      totalPages: number;
      currentPageNumber?: number;
    } = {
      products: [],
      aggregations: [],
      translations: {},
      count: 0,
      totalCount: 0,
      totalPages: 0,
      currentPageNumber: currentPageNumber,
    };

    response.data.forEach(product => {
      const thumbnail =
        product.images?.[0]?.thumbnailUrl ?? '/images/noimage.jpeg';
      const original =
        product.images?.[0]?.originalUrl ?? '/images/noimage.jpeg';

      transformedPrdouctsResponse.products.push({
        id: product.id,
        AvailableOn: product.availableOn,
        quantityLimit: product.quantityLimit,
        variantId: product.variantId,
        slug: product.slug,
        name: product.name,
        price: 0,
        displayPrice: product.displayPrice,
        description: product.description,
        sku: product.sku,

        isHavingBulkDiscount: false,

        bulkDiscount: {
          title: '',
          description: '',
        },
        inStock: true,
        breadcrumb: [],
        images: [
          {
            original: original,
            thumbnail: thumbnail,
            id: '',
          },
        ],
        languageBasedVariants: [],
        assets: [],
        materialType: product.materialType,
        isCustomizable: false,
        discontinueOn: product.discontinueOn,
        dimensions: '',
      });
    });

    transformedPrdouctsResponse.aggregations = this.buildAggregation(
      response.aggregations
    );
    transformedPrdouctsResponse.translations = response.translations || {};

    transformedPrdouctsResponse.count = response.meta?.count;
    transformedPrdouctsResponse.totalCount = response.meta?.total_count;
    transformedPrdouctsResponse.totalPages = response.meta?.total_pages;

    console.log('transformedPrdouctsResponse', transformedPrdouctsResponse);

    return transformedPrdouctsResponse;
  }

  buildProductDetailsForClient(
    response: ProductCache,
    quantityLimitResponse: number[]
  ) {
    const { publicRuntimeConfig } = getConfig();
    const product: Product = this.buildEmptyProduct();

    const productQuantityLimit = quantityLimitResponse.filter(
      (limitValue: number | null) => limitValue !== null
    );

    const customProductQuantityLimit = productQuantityLimit;

    product.id = response.id;
    product.variantId = response.variantId;
    product.name = response.name;
    product.sku = response.sku;
    product.displayPrice = response.displayPrice;
    product.description = response.description;
    product.ingredients = response.ingredients;
    product.instructions = response.instructions;
    product.returnPolicy =
      response.returnPolicy || publicRuntimeConfig.DEFAULT_RETURN_POLICY;
    product.disclaimerText = response.disclaimerText;
    product.quantityLimit = customProductQuantityLimit;
    product.inStock = isProductInStock(
      response.backorderable,
      response.inStock
    );

    product.isCustomizable = response.isCustomizable;
    product.materialType = response.materialType;
    product.discontinueOn = response.discontinueOn;
    product.dimensions = response.dimensions;

    if (response.images && response.images.length > 0) {
      response.images.forEach(productImage => {
        const thumbnail = productImage.thumbnailUrl;
        const original = productImage.originalUrl;

        product.images.push({
          original: original,
          thumbnail: thumbnail,
          id: '',
        });
      });
    } else {
      product.images.push({
        id: '-1',
        original: '/images/noimage.jpeg',
        thumbnail: '/images/noimage.jpeg',
      });
    }

    if (response.videos && response.videos.length > 0) {
      response.videos.forEach(video => {
        product.images.push({
          original: video.originalUrl,
          thumbnail: video.thumbnailUrl,
          id: '',
        });
      });
    }

    const breadcrumbs = response.breadcrumbs;
    if (breadcrumbs) {
      product.breadcrumb = this.buildProductBreadcrumbFromCache(breadcrumbs);
    }

    if (response.assets?.length > 0) {
      response.assets.forEach(asset => {
        product.assets.push({
          title: asset.title,
          url: `${publicRuntimeConfig.IMAGE_HOST}${asset.url}`,
        });
      });
    }

    if (response.productGroup) {
      product.productGroup = response.productGroup;
    }

    return product;
  }

  buildEmptyProduct() {
    return {
      id: '',
      variantId: '',
      name: '',
      sku: '',
      slug: '',
      price: 0.0,
      description: '',
      displayPrice: '',
      quantityLimit: [],
      inStock: true,
      isHavingBulkDiscount: false,
      bulkDiscount: {
        title: '',
        description: '',
      },
      images: [],
      breadcrumb: [],
      languageBasedVariants: [],
      assets: [],
      materialType: '',
      isCustomizable: false,
      discontinueOn: '',
      dimensions: '',
      AvailableOn: '',
    };
  }

  getProductPrice(country: string, priceObject: { [country: string]: string }) {
    return priceObject[country];
  }

  buildProductBreadcrumbFromCache(breadcrumbs: TBreadCrumb[]) {
    const breadcrumbItems = [...breadcrumbs].sort(
      (item1, item2) => item1.level - item2.level
    );

    return breadcrumbItems;
  }

  buildAggregation(aggregations: ProductAggregation[]) {
    return aggregations;
  }

  buildFilterParams(filters: AppliedFacetFilter[]): string {
    const productListFilter = this.groupByKeys(filters);

    const filterParams = new URLSearchParams();
    Object.entries(productListFilter).forEach(([key, value]) => {
      console.log(`Key: ${key}, Value: ${value}`);
      filterParams.append(`filter_by[${key}]`, value);
    });
    return filterParams.toString();
  }

  buildAggregationParams(
    taxonSlug: string | undefined,
    isDsoStore: boolean
  ): string {
    if (!taxonSlug || taxonSlug.length === 0) {
      return '';
    }

    const aggregationParams = new URLSearchParams();

    if (taxonSlug.includes('practice-marketing') && isNAStore()) {
      aggregationParams.append(
        'aggregate_by',
        'asset-type,messaging,product,customizable,printable,download,language'
      );
    } else {
      aggregationParams.append('aggregate_by', 'child-taxon-names');
    }
    return aggregationParams.toString();
  }

  buildSearchParams(searchCriteria: SearchCriteria): string {
    const searchParams = new URLSearchParams();

    if (searchCriteria.taxonSlug) {
      searchParams.append('search_by[taxon_slug]', searchCriteria.taxonSlug);
    }

    if (searchCriteria.variantIds) {
      searchParams.append('search_by[variant_ids]', searchCriteria.variantIds);
    }

    if (searchCriteria.skus) {
      searchParams.append('search_by[skus]', searchCriteria.skus);
    }

    if (searchCriteria.name) {
      searchParams.append('search_by[name]', searchCriteria.name);
    }
    return searchParams.toString();
  }

  groupByKeys(filters: AppliedFacetFilter[]) {
    return filters.reduce(
      (
        grouped: {
          [key: string]: string;
        },
        { filterKey, filterValue }
      ) => {
        if (!grouped[filterKey]) {
          grouped[filterKey] = filterValue;
        } else {
          grouped[filterKey] += `, ${filterValue}`;
        }
        return grouped;
      },
      {}
    );
  }
}
